import { createAsyncThunk, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { getCurrentCustomer } from '@/api/requests/customer';
import { Customer } from '@/actions/dtos/subscription';
import { fetchCurrentProfile, updateProfile } from '@/api/requests/persons';
import {
  Member,
  Organization,
  OrganizationDetails,
  OrganizationDetailsData,
  Person,
  PersonDetailsData,
} from '@/actions/dtos/organisation';
import {
  autofillOrganizationDetails,
  changeOrganizationName,
  fetchCurrentOrganization,
  getOrganizationDetails,
  upsertOrganizationDetails,
} from '@/api/requests/organizations';
import { AppDispatch, RootState } from '@/slices/store';
import { toast } from 'react-toastify';
import {
  addMemberToOrganization,
  changeMemberRole,
  deleteMemberFromOrganization,
  fetchCurrentMember,
} from '@/api/requests/members';
import Cookies from 'js-cookie';

interface AccountState {
  customer: Customer | null;
  person: Person | null;
  organization: Organization | null;
  organizationDetails: OrganizationDetails | null;
  member: Member | null;
  loading: boolean;
  error: string | null;
}

const initialState: AccountState = {
  customer: null,
  person: null,
  organization: null,
  organizationDetails: null,
  member: null,
  loading: false,
  error: null,
};

export const fetchCustomer = createAsyncThunk<Customer, void, { rejectValue: string }>(
  'account/fetchCustomer',
  async (_, { rejectWithValue }) => {
    try {
      return await getCurrentCustomer();
    } catch (error: any) {
      return rejectWithValue(error.message ?? 'Failed to fetch customer');
    }
  },
);

export const fetchPerson = createAsyncThunk<Person, string | undefined, { rejectValue: string }>(
  'account/fetchPerson',
  async (accessToken = undefined, { rejectWithValue }) => {
    try {
      const person = await fetchCurrentProfile(accessToken);
      Cookies.set('person_onboarded', person.onboarded.toString(), { path: '/' });
      return person;
    } catch (error: any) {
      return rejectWithValue(error.message ?? 'Failed to fetch person');
    }
  },
);

export const fetchOrganization = createAsyncThunk<Organization, void, { rejectValue: string }>(
  'account/fetchOrganization',
  async (_, { rejectWithValue }) => {
    try {
      const organization = await fetchCurrentOrganization();
      Cookies.set('organization_onboarded', organization.onboarded.toString(), { path: '/' });
      return organization;
    } catch (error: any) {
      return rejectWithValue(error.message ?? 'Failed to fetch organization');
    }
  },
);

export const fetchMember = createAsyncThunk<Member, void, { rejectValue: string }>(
  'account/fetchMember',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchCurrentMember();
    } catch (error: any) {
      return rejectWithValue(error.message ?? 'Failed to fetch current member');
    }
  },
);

export const updateOrganizationDetails = createAsyncThunk<
  OrganizationDetails,
  { id: string; data: OrganizationDetailsData },
  { rejectValue: string }
>('account/updateOrganizationDetails', async ({ id, data }, { rejectWithValue }) => {
  try {
    const organizationDetails = await upsertOrganizationDetails(id, data);
    toast.success('Erfolgreiche Aktualisierung Ihres Unternehmens');
    return organizationDetails;
  } catch (error: any) {
    toast.error('Fehler beim Aktualisierung Ihres Unternehmens');
    return rejectWithValue(error.message || 'Failed to update organization details');
  }
});

export const fetchOrganizationDetails = createAsyncThunk<OrganizationDetails | null, string, { rejectValue: string }>(
  'account/fetchOrganizationDetails',
  async (id, { rejectWithValue }) => {
    try {
      return await getOrganizationDetails(id);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch organization details');
    }
  },
);

export const autofillOrganizationData = createAsyncThunk<OrganizationDetails, string, { rejectValue: string }>(
  'account/autofillOrganizationDetails',
  async (id, { rejectWithValue }) => {
    try {
      return await autofillOrganizationDetails(id);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to autofill organization details');
    }
  },
);

export const updatePersonProfile = createAsyncThunk<
  Person,
  { id: string; data: PersonDetailsData },
  { rejectValue: string }
>('account/updatePersonProfile', async ({ id, data }, { rejectWithValue }) => {
  try {
    const person = await updateProfile(id, data);
    Cookies.set('person_onboarded', person.onboarded.toString(), { path: '/' });
    return person;
  } catch (error: any) {
    return rejectWithValue(error.message ?? 'Failed to update person profile');
  }
});

export const updateOrganizationName = createAsyncThunk<
  Organization,
  string,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>('account/updateOrganizationName', async (companyName, { rejectWithValue }) => {
  try {
    const organization = await changeOrganizationName(companyName);
    Cookies.set('organization_onboarded', organization.onboarded.toString(), { path: '/' });
    return organization;
  } catch (error: any) {
    return rejectWithValue(error.response?.data?.message ?? 'Failed to update organization name');
  }
});

export const addNewMemberToOrganization = createAsyncThunk<
  Member,
  { organizationId: string; email: string },
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>('account/addMemberToOrganization', async ({ organizationId, email }, { rejectWithValue }) => {
  try {
    const member = await addMemberToOrganization({ organizationId, role: 'MEMBER', email });
    toast.success('Einladung zur Aufnahme in die Organisation wurde erfolgreich gesendet!');
    return member;
  } catch (error: any) {
    toast.error('Es konnte kein neues Mitglied zur Organisation hinzugefügt werden');
    return rejectWithValue(error.response?.data?.message ?? 'Failed to add new member to organization');
  }
});

export const removeMemberFromOrganization = createAsyncThunk<
  void,
  { memberId: string },
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>('account/removeMemberFromOrganization', async ({ memberId }, { rejectWithValue }) => {
  try {
    await deleteMemberFromOrganization(memberId);
    toast.success('Das Mitglied wurde erfolgreich aus der Organisation entfernt!');
  } catch (error: any) {
    toast.error('Beim Entfernen des Mitglieds aus der Organisation ist ein Fehler aufgetreten.');
    return rejectWithValue(error.response?.data?.message ?? 'Failed to remove member from organization');
  }
});

export const changeMemberRoleInOrganization = createAsyncThunk<
  void,
  { memberId: string; role: string; enabled: boolean; actionType: 'ROLE_CHANGE' | 'STATUS_CHANGE' },
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>('account/changeMemberRoleInOrganization', async ({ memberId, role, enabled, actionType }, { rejectWithValue }) => {
  try {
    await changeMemberRole(memberId, role, enabled);

    if (actionType === 'STATUS_CHANGE') {
      toast.success(enabled ? 'Benutzer wurde erfolgreich aktiviert!' : 'Benutzer wurde erfolgreich deaktiviert!');
    } else if (actionType === 'ROLE_CHANGE') {
      toast.success('Die Rolle wurde erfolgreich geändert!');
    }
  } catch (error: any) {
    if (actionType === 'STATUS_CHANGE') {
      toast.error(`Beim ${enabled ? 'Aktivieren' : 'Deaktivieren'} des Benutzers ist ein Fehler aufgetreten.`);
    } else if (actionType === 'ROLE_CHANGE') {
      toast.error('Beim Ändern der Rolle ist ein Fehler aufgetreten.');
    }
    return rejectWithValue(error.response?.data?.message ?? 'Failed to change member role or state');
  }
});

function onPending(state: AccountState) {
  state.loading = true;
  state.error = null;
}

function onRejected(state: AccountState, action: PayloadAction<string | undefined>) {
  state.loading = false;
  state.error = action.payload ?? 'Unknown error';
}

export const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    clearAccountData: (state) => {
      state.customer = null;
      state.organization = null;
      state.organizationDetails = null;
      state.person = null;
      state.member = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateOrganizationDetails.fulfilled, (state, action: PayloadAction<OrganizationDetails>) => {
        state.loading = false;
        state.organizationDetails = action.payload;
      })
      .addCase(autofillOrganizationData.fulfilled, (state, action: PayloadAction<OrganizationDetails>) => {
        state.loading = false;
        state.organizationDetails = action.payload;
      })
      .addCase(fetchOrganizationDetails.fulfilled, (state, action: PayloadAction<OrganizationDetails | null>) => {
        state.loading = false;
        state.organizationDetails = action.payload;
      })
      .addCase(fetchCustomer.fulfilled, (state, action: PayloadAction<Customer>) => {
        state.loading = false;
        state.customer = action.payload;
      })
      .addCase(fetchPerson.fulfilled, (state, action: PayloadAction<Person>) => {
        state.loading = false;
        state.person = action.payload;
      })
      .addCase(fetchMember.fulfilled, (state, action: PayloadAction<Member>) => {
        state.loading = false;
        state.member = action.payload;
      })
      .addCase(updatePersonProfile.fulfilled, (state, action: PayloadAction<Person>) => {
        state.loading = false;
        state.person = action.payload;
      })
      .addCase(fetchOrganization.fulfilled, (state, action: PayloadAction<Organization>) => {
        state.loading = false;
        state.organization = action.payload;
      })
      .addCase(updateOrganizationName.fulfilled, (state, action: PayloadAction<Organization>) => {
        state.loading = false;
        state.organization = action.payload;
      })
      .addMatcher(
        isAnyOf(
          fetchCustomer.pending,
          fetchPerson.pending,
          fetchOrganization.pending,
          fetchMember.pending,
          updateOrganizationDetails.pending,
          autofillOrganizationData.pending,
          fetchOrganizationDetails.pending,
          updatePersonProfile.pending,
          updateOrganizationName.pending,
          addNewMemberToOrganization.pending,
          removeMemberFromOrganization.pending,
          changeMemberRoleInOrganization.pending,
        ),
        onPending,
      )
      .addMatcher(
        isAnyOf(
          fetchCustomer.rejected,
          fetchPerson.rejected,
          fetchOrganization.rejected,
          fetchMember.rejected,
          updateOrganizationDetails.rejected,
          autofillOrganizationData.rejected,
          fetchOrganizationDetails.rejected,
          updatePersonProfile.rejected,
          updateOrganizationName.rejected,
          addNewMemberToOrganization.rejected,
          removeMemberFromOrganization.rejected,
          changeMemberRoleInOrganization.rejected,
        ),
        onRejected,
      );
  },
});

export const { clearAccountData } = accountSlice.actions;
export default accountSlice.reducer;
